"use client";

// ПОИСК ПО ПОРТАЛУ — ОДИН ЭЛЕМЕНТ, а не поле, открывающее второй.
//
// ⚠️ Максим, 03.09.2026: «это может быть один компонент, который просто
// раскрывается, а не один, который потом при нажатии открывает второй».
// Раньше строка в шапке была кнопкой, а сам поиск — отдельной палитрой посреди
// экрана; на месте строки она оказывалась только потому, что ей передавали
// рамку. Здесь строка и есть поиск: та же коробка растёт в ширину, под ней
// раскрывается список. Ничего не открывается поверх.
//
// Что ищем: разделы, коллег и действия — на месте; задачи и документы базы
// знаний — на сервере (`/search`, видимость там же, что в самих модулях);
// статьи справки лежат в коде и ищутся по всему тексту.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { ArrowUpRight, ClipboardList, FileText, LifeBuoy, MessagesSquare, Wrench } from "lucide-react";

import { IconSearch, IconSparkles } from "@/components/ui/icons";
import { useAuth } from "@/components/auth-provider";
import { useDepartments } from "@/lib/dept-colors";
import { deptUsesTabPerms } from "@/lib/access";
import { api } from "@/lib/api";
import { HELP_ARTICLES, articlePlainText } from "@/lib/help-content";
import type { ChatUser, PortalSearch as PortalSearchResult } from "@/lib/types";
import { Z } from "@/lib/z";

type Находка = {
  id: string;
  label: string;
  sub?: string;
  cat: string;
  icon: React.ReactNode;
  color: string;
  /** Пришло с сервера: местным фильтром по подстроке не отсеиваем — сервер ищет
   *  и по описанию задачи, и по тексту документа, а в строке этих слов нет. */
  remote?: boolean;
  run: () => void;
};

/** Действия открывают панель в углу — она живёт в другом компоненте, поэтому
 *  просим её событием. Второй копии панели заводить нельзя. */
const действие = (tab: string, action: string | null) =>
  window.dispatchEvent(new CustomEvent("yuc:fab-action", { detail: { tab, action } }));

export function PortalSearch({ className = "" }: { className?: string }) {
  const router = useRouter();
  const { user } = useAuth();
  const departments = useDepartments();
  const обёртка = useRef<HTMLDivElement>(null);
  const поле = useRef<HTMLInputElement>(null);
  const список = useRef<HTMLDivElement>(null);

  const [открыт, setОткрыт] = useState(false);
  const [q, setQ] = useState("");
  const [выбран, setВыбран] = useState(0);
  const [коллеги, setКоллеги] = useState<ChatUser[]>([]);
  const [сНервера, setСервера] = useState<PortalSearchResult>({ tasks: [], docs: [] });
  const [идёт, setИдёт] = useState(false);

  const открыть = useCallback(() => {
    setОткрыт(true);
    if (!коллеги.length) api.chatUsers().then(setКоллеги).catch(() => { /* без коллег переживём */ });
    setTimeout(() => поле.current?.focus(), 10);
  }, [коллеги.length]);

  const закрыть = useCallback(() => { setОткрыт(false); setQ(""); поле.current?.blur(); }, []);

  // Ctrl/Cmd+K — та же строка, а не отдельное окно.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        if (открыт) закрыть(); else открыть();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [открыт, открыть, закрыть]);

  // Клик мимо — свернуть. Затемнения нет: это выпадающий список, а не окно.
  useEffect(() => {
    if (!открыт) return;
    const вне = (e: MouseEvent) => {
      if (!обёртка.current?.contains(e.target as Node)) закрыть();
    };
    document.addEventListener("mousedown", вне);
    return () => document.removeEventListener("mousedown", вне);
  }, [открыт, закрыть]);

  // Сервер: дребезг 250 мс, короче двух знаков не ходим.
  useEffect(() => {
    const запрос = q.trim();
    if (!открыт || запрос.length < 2) { setСервера({ tasks: [], docs: [] }); setИдёт(false); return; }
    setИдёт(true);
    let живо = true;
    const t = setTimeout(() => {
      api.searchPortal(запрос)
        .then((r) => { if (живо) setСервера(r); })
        .catch(() => { if (живо) setСервера({ tasks: [], docs: [] }); })
        .finally(() => { if (живо) setИдёт(false); });
    }, 250);
    return () => { живо = false; clearTimeout(t); };
  }, [q, открыт]);

  const разделы = useMemo(() => {
    if (!user) return [] as { path: string; label: string }[];
    // ⚠️ ОТДЕЛ ПОКАЗЫВАЕМ, ТОЛЬКО ЕСЛИ В НЁМ ЧТО-ТО ОТКРЫТО (Максим, 04.09.2026:
    // «в поиске должен производиться поиск только согласно правам и ролям»).
    // Раньше поиск предлагал перейти в свой отдел просто по записи в карточке —
    // человек попадал в раздел, где ему не открыта ни одна вкладка.
    const свои = departments.filter((d) => d.key !== "system")
      .filter((d) => user.role === "platform_admin"
        || (d.key === user.department && deptUsesTabPerms(user, d.key))
        || deptUsesTabPerms(user, d.key));
    const t = [{ path: "/home", label: "Главная" }, { path: "/cabinet", label: "Кабинет" }];
    for (const d of свои) t.push({ path: `/departments/${d.key}`, label: `Отдел «${d.title}»` });
    t.push({ path: "/team", label: "Команда" }, { path: "/help", label: "Инструкции" });
    if (user.role === "platform_admin") t.push({ path: "/settings", label: "Настройки" });
    return t;
  }, [departments, user]);

  const всё = useMemo<Находка[]>(() => {
    const действия: Находка[] = [
      { id: "a-ai", label: "Спросить AI", cat: "Действия", color: "var(--primary)", icon: <IconSparkles size={15} />, run: () => действие("ai", "new-chat") },
      { id: "a-task", label: "Новая задача", cat: "Действия", color: "var(--primary)", icon: <ClipboardList size={15} />, run: () => действие("tasks", "new-task") },
      { id: "a-chat", label: "Чат с коллегами", cat: "Действия", color: "var(--primary)", icon: <MessagesSquare size={15} />, run: () => действие("chat", null) },
      { id: "a-req", label: "Заявки на доработку", cat: "Действия", color: "var(--primary)", icon: <Wrench size={15} />, run: () => действие("requests", null) },
    ];
    const нав: Находка[] = разделы.map((t) => ({
      id: `n-${t.path}`, label: `Открыть: ${t.label}`, sub: t.path, cat: "Разделы",
      color: "var(--primary)", icon: <ArrowUpRight size={15} />, run: () => router.push(t.path),
    }));
    const люди: Находка[] = коллеги.filter((u) => u.id !== user?.id).map((u) => ({
      id: `u-${u.id}`, label: u.name, sub: u.position || undefined, cat: "Коллеги",
      color: u.color || "#1f9bff", icon: <MessagesSquare size={15} />,
      run: () => window.dispatchEvent(new CustomEvent("yuc:fab-action", { detail: { tab: "chat", action: null, userId: u.id } })),
    }));
    const задачи: Находка[] = сНервера.tasks.map((t) => ({
      id: `t-${t.id}`, label: t.title, sub: t.snippet || undefined, cat: "Задачи",
      color: "#f59e0b", icon: <ClipboardList size={15} />, remote: true,
      run: () => router.push(`/cabinet?task=${t.parent_task_id ?? t.id}`),
    }));
    const доки: Находка[] = сНервера.docs.map((d) => ({
      id: `d-${d.id}`, label: `${d.emoji ? `${d.emoji} ` : ""}${d.title}`,
      sub: d.snippet || d.category || undefined, cat: "База знаний",
      color: "#0ea5e9", icon: <FileText size={15} />, remote: true,
      run: () => router.push(`/kb?doc=${d.id}`),
    }));
    const запрос = q.trim().toLowerCase();
    const справка: Находка[] = запрос.length < 2 ? [] : HELP_ARTICLES
      .filter((a) => `${a.title} ${a.description} ${articlePlainText(a)}`.toLowerCase().includes(запрос))
      .slice(0, 6)
      .map((a) => ({
        id: `h-${a.slug}`, label: a.title, sub: a.description, cat: "Инструкции",
        color: "#10b981", icon: <LifeBuoy size={15} />, remote: true,
        run: () => router.push(`/help/${a.slug}`),
      }));
    // Порядок: сначала точное (разделы, коллеги, действия), потом содержимое.
    return [...действия, ...нав, ...люди, ...задачи, ...доки, ...справка];
  }, [разделы, коллеги, сНервера, q, router, user?.id]);

  const запрос = q.trim().toLowerCase();
  const видимые = useMemo(() => (запрос
    ? всё.filter((c) => c.remote || `${c.label} ${c.sub ?? ""} ${c.cat}`.toLowerCase().includes(запрос))
    : всё.filter((c) => !c.remote)), [всё, запрос]);

  useEffect(() => { setВыбран(0); }, [запрос]);
  useEffect(() => {
    список.current?.querySelector('[data-sel="true"]')?.scrollIntoView({ block: "nearest" });
  }, [выбран, видимые.length]);

  const пуск = (c?: Находка) => { if (c) { закрыть(); c.run(); } };

  return (
    <div ref={обёртка} className={`relative h-9 w-9 md:w-[190px] ${className}`}>
      <div
        style={открыт ? { zIndex: Z.COMMAND } : undefined}
        className={`absolute right-0 top-0 overflow-hidden rounded-[var(--radius-md)] border border-[var(--border)] transition-[width,box-shadow] duration-150 ${
          открыт
            ? "w-[min(460px,calc(100vw-24px))] bg-[var(--surface)] elev-modal"
            : "w-9 bg-transparent md:w-[190px]"
        }`}>
        <div className="flex h-9 items-center gap-2 px-2.5">
          <IconSearch size={15} className="shrink-0 text-[var(--textMuted)]" />
          <input
            ref={поле}
            value={q}
            onFocus={() => { if (!открыт) открыть(); }}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") { e.preventDefault(); setВыбран((s) => Math.min(s + 1, видимые.length - 1)); }
              else if (e.key === "ArrowUp") { e.preventDefault(); setВыбран((s) => Math.max(s - 1, 0)); }
              else if (e.key === "Enter") { e.preventDefault(); пуск(видимые[выбран]); }
              else if (e.key === "Escape") { e.preventDefault(); закрыть(); }
            }}
            placeholder={открыт ? "Задача, документ, раздел или коллега…" : "Поиск по порталу"}
            aria-label="Поиск по порталу"
            className="min-w-0 flex-1 bg-transparent text-[12px] text-[var(--text)] placeholder:text-[var(--textMuted)] focus:outline-none"
          />
          <kbd className="hidden shrink-0 rounded-[var(--radius-md)] border border-[var(--border)] px-1.5 py-0.5 text-[11px] text-[var(--textMuted)] lg:inline">
            {открыт ? "ESC" : "Ctrl K"}
          </kbd>
        </div>

        {открыт && (
          <div ref={список} className="max-h-[60dvh] overflow-y-auto border-t border-[var(--border)] p-1.5">
            {видимые.length === 0 && (
              <p className="py-8 text-center text-[12px] text-[var(--textMuted)]">
                {идёт ? "Ищем…" : запрос.length === 1 ? "Наберите хотя бы два знака" : "Ничего не найдено"}
              </p>
            )}
            {видимые.map((c, i) => {
              const на = i === выбран;
              const шапка = i === 0 || видимые[i - 1].cat !== c.cat;
              return (
                <div key={c.id}>
                  {шапка && <div className="field-label px-2.5 pb-1 pt-2">{c.cat}</div>}
                  <button type="button" data-sel={на} onMouseEnter={() => setВыбран(i)} onClick={() => пуск(c)}
                    // ⚠️ ВЫДЕЛЕНИЕ СТРОКИ — ОБЩИЙ СЕРЫЙ `--hover`, как везде в
                    // портале (карточка доски, строка подзадачи, строка
                    // таблицы). Здесь оставалась фиолетовая заливка от прежней
                    // палитры (Максим, 03.09.2026: «можно как везде сделать
                    // сереньким выделение активной строки»).
                    style={{ boxShadow: "none", ...(на ? { background: "var(--hover)" } : {}) }}
                    className="glass-flat flex w-full items-center gap-2.5 rounded-[var(--radius-md)] px-2.5 py-2 text-left transition">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full"
                      style={{ background: `color-mix(in srgb, ${c.color} 16%, transparent)`, color: c.color }}>{c.icon}</span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[12px] text-[var(--text)]">{c.label}</span>
                      {c.sub && <span className="block truncate text-[11px] text-[var(--textMuted)]">{c.sub}</span>}
                    </span>
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
